<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <Modal
    :isVisible="modal.isSubmitting"
    :promptText="isCreateMode ? 'Create this state health organization?' : 'Save the state health organization changes?'"
    yesText="Save"
    noText="Cancel"
    @yes="submit"
    @no="closeModal"
  />

  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">State Access Control</p>
        <h1 class="page-title">{{ isCreateMode ? 'Create state health organization' : (formData.name || 'Edit state health organization') }}</h1>
        <p class="page-subtitle">
          Define the state scope first, then control exactly which clinics are watchable by this state organization.
        </p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/state-access')">
        <i class="fas fa-arrow-left"></i> Back to state access
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">State organization basics</h2>
              <p class="section-description">The state choice is canonical and controls which clinics can be assigned below.</p>
            </div>
            <span v-if="!isCreateMode" class="badge-soft" :class="{ success: formData.is_active }">
              {{ formData.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Name <span class="required">*</span></label>
              <input class="ui-input" v-model="formData.name" placeholder="e.g. Kedah State Health Organization">
            </div>

            <div class="form-section">
              <label class="field-label">State <span class="required">*</span></label>
              <select class="ui-select" v-model="formData.state_name" @change="handleStateChange">
                <option value="">Choose state</option>
                <option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
              </select>
            </div>

            <div class="form-section">
              <label class="field-label">State code</label>
              <input class="ui-input" v-model="formData.state_code" placeholder="e.g. MY-KDH">
            </div>

            <div class="form-section">
              <label class="field-label">Contact email</label>
              <input type="email" class="ui-input" v-model="formData.contact_email" placeholder="state-org@example.org">
            </div>

            <div class="form-section">
              <label class="field-label">Contact phone</label>
              <input class="ui-input" v-model="formData.contact_phone" placeholder="Digits only" @input="cleanPhone">
            </div>

            <div class="form-section" v-if="!isCreateMode">
              <label class="field-label">Status</label>
              <button class="status-toggle" :class="{ active: formData.is_active }" @click="formData.is_active = !formData.is_active">
                {{ formData.is_active ? 'Active' : 'Inactive' }}
              </button>
            </div>

            <div class="form-section full-width">
              <label class="field-label">Description</label>
              <textarea class="ui-textarea" v-model="formData.description" placeholder="Optional notes about this state organization"></textarea>
            </div>
          </div>

          <div class="ui-actions">
            <button class="ui-button secondary" @click="$router.push('/state-access')">Cancel</button>
            <button class="ui-button primary" @click="openSubmitModal()">
              <i class="fas fa-save"></i> {{ isCreateMode ? 'Create state org' : 'Save basics' }}
            </button>
          </div>
        </section>

        <section class="mt-4" v-if="!isCreateMode">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Watchable clinics</h2>
              <p class="section-description">Only clinics in the selected state can be assigned. These clinics will be visible to this state organization.</p>
            </div>
            <span class="optional-pill">{{ selectedClinicIds.length }} selected</span>
          </div>

          <div class="toolbar toolbar--compact">
            <div class="toolbar-search">
              <input class="ui-input" v-model="clinicSearch" placeholder="Search clinic, city, or address">
            </div>
            <div class="toolbar-actions">
              <button class="ui-button secondary" @click="selectAllVisible">Select visible</button>
              <button class="ui-button secondary" @click="clearAllVisible">Clear visible</button>
              <button class="ui-button primary" @click="saveWatchableClinics" :disabled="isSavingClinics">
                <i class="fas" :class="isSavingClinics ? 'fa-spinner fa-spin' : 'fa-eye'"></i>
                {{ isSavingClinics ? 'Saving...' : 'Save watchable clinics' }}
              </button>
            </div>
          </div>

          <div class="stats-grid stats-grid--compact">
            <div class="stat-card">
              <span class="stat-label">Clinics in state</span>
              <strong class="stat-value">{{ assignableClinics.length }}</strong>
            </div>
            <div class="stat-card">
              <span class="stat-label">Visible after filter</span>
              <strong class="stat-value">{{ filteredClinics.length }}</strong>
            </div>
            <div class="stat-card">
              <span class="stat-label">Watchable clinics</span>
              <strong class="stat-value">{{ selectedClinicIds.length }}</strong>
            </div>
          </div>

          <div class="table-card mt-3">
            <table class="ui-table">
              <thead>
                <tr>
                  <th class="text-start">Watchable</th>
                  <th class="text-start">Clinic</th>
                  <th class="text-start">Location</th>
                  <th class="text-start">Address</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="clinic in filteredClinics" :key="clinic.LocationUuid">
                  <td>
                    <label class="checkbox-wrap">
                      <input
                        type="checkbox"
                        :checked="selectedClinicIds.includes(clinic.LocationUuid)"
                        @change="toggleClinicSelection(clinic.LocationUuid, $event.target.checked)"
                      >
                      <span>{{ selectedClinicIds.includes(clinic.LocationUuid) ? 'Yes' : 'No' }}</span>
                    </label>
                  </td>
                  <td>
                    <span class="table-primary">{{ clinic.ClinicName || 'Unnamed clinic' }}</span>
                    <span class="table-secondary">{{ clinic.LocationUuid }}</span>
                  </td>
                  <td>
                    <span class="table-primary">{{ [clinic.City, clinic.State].filter(Boolean).join(', ') || 'No city/state' }}</span>
                    <span class="table-secondary">{{ clinic.OrganizationUuid || 'No organization UUID' }}</span>
                  </td>
                  <td>
                    <span class="table-primary">{{ clinic.Address || 'No address' }}</span>
                  </td>
                </tr>
                <tr v-if="filteredClinics.length === 0">
                  <td colspan="4">
                    <div class="empty-state">
                      <strong>No clinics available.</strong>
                      <p>Assign a state and ensure clinics exist in that canonical state.</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading.vue';
import Modal from '@/components/Modal.vue';
import {
  createStateHealthOrg,
  getAssignableClinics,
  getCanonicalStates,
  getStateHealthOrg,
  updateStateHealthOrg,
  updateWatchableClinics,
} from '@/services/stateHealthOrgServices';

const createDefaultForm = () => ({
  name: '',
  state_name: '',
  state_code: '',
  contact_email: '',
  contact_phone: '',
  description: '',
  is_active: true,
});

export default {
  name: 'StateHealthOrgEdit',
  components: {
    Loading,
    Modal,
  },
  data() {
    return {
      formData: createDefaultForm(),
      states: [],
      assignableClinics: [],
      selectedClinicIds: [],
      clinicSearch: '',
      isLoading: false,
      isSavingClinics: false,
      validationError: '',
      modal: {
        isSubmitting: false,
      },
    };
  },
  computed: {
    isCreateMode() {
      return !this.$route.params.id;
    },
    currentUuid() {
      return this.$route.params.id || '';
    },
    filteredClinics() {
      const query = this.clinicSearch.trim().toLowerCase();
      if (!query) {
        return this.assignableClinics;
      }
      return this.assignableClinics.filter((clinic) => [
        clinic.ClinicName,
        clinic.City,
        clinic.State,
        clinic.Address,
      ].some((value) => String(value || '').toLowerCase().includes(query)));
    },
  },
  async created() {
    await this.loadStates();
    if (!this.isCreateMode) {
      await this.loadStateHealthOrg();
      await this.loadAssignableClinics();
    }
  },
  methods: {
    async loadStates() {
      const result = await getCanonicalStates();
      this.states = result.data || [];
    },
    async loadStateHealthOrg() {
      this.isLoading = true;
      try {
        const result = await getStateHealthOrg(this.currentUuid);
        const data = result.data || {};
        this.formData = {
          name: data.Name || '',
          state_name: data.StateName || '',
          state_code: data.StateCode || '',
          contact_email: data.ContactEmail || '',
          contact_phone: data.ContactPhone || '',
          description: data.Description || '',
          is_active: data.isActive !== false,
        };
      } finally {
        this.isLoading = false;
      }
    },
    async loadAssignableClinics() {
      if (!this.currentUuid) {
        return;
      }
      this.isLoading = true;
      try {
        const result = await getAssignableClinics(this.currentUuid);
        this.assignableClinics = result.data || [];
        this.selectedClinicIds = this.assignableClinics
          .filter((clinic) => clinic.isWatchable)
          .map((clinic) => clinic.LocationUuid);
      } finally {
        this.isLoading = false;
      }
    },
    handleStateChange() {
      if (!this.formData.state_code && this.formData.state_name) {
        this.formData.state_code = this.formData.state_name;
      }
    },
    cleanPhone(event) {
      this.formData.contact_phone = event.target.value.replace(/\D/g, '');
    },
    validateBasics() {
      if (!this.formData.name.trim()) {
        this.validationError = 'State health organization name is required.';
        return false;
      }
      if (!this.formData.state_name) {
        this.validationError = 'Choose the canonical state for this organization.';
        return false;
      }
      this.validationError = '';
      return true;
    },
    openSubmitModal() {
      if (this.validateBasics()) {
        this.modal.isSubmitting = true;
      }
    },
    closeModal() {
      this.modal.isSubmitting = false;
    },
    async submit() {
      if (!this.validateBasics()) {
        return;
      }

      this.modal.isSubmitting = false;
      this.isLoading = true;
      try {
        const payload = {
          ...this.formData,
        };

        if (this.isCreateMode) {
          const response = await createStateHealthOrg(payload);
          const createdUuid = response.data?.StateHealthOrgUuid;
          if (createdUuid) {
            this.$router.push({ name: 'state-access-edit', params: { id: createdUuid } });
            return;
          }
        } else {
          await updateStateHealthOrg(this.currentUuid, payload);
          await this.loadStateHealthOrg();
        }
      } catch (error) {
        this.validationError = error.response?.data?.detail || 'Unable to save the state health organization.';
      } finally {
        this.isLoading = false;
      }
    },
    toggleClinicSelection(locationUuid, isChecked) {
      if (isChecked) {
        if (!this.selectedClinicIds.includes(locationUuid)) {
          this.selectedClinicIds = [...this.selectedClinicIds, locationUuid];
        }
        return;
      }

      this.selectedClinicIds = this.selectedClinicIds.filter((uuid) => uuid !== locationUuid);
    },
    selectAllVisible() {
      const visibleIds = this.filteredClinics.map((clinic) => clinic.LocationUuid);
      this.selectedClinicIds = [...new Set([...this.selectedClinicIds, ...visibleIds])];
    },
    clearAllVisible() {
      const visibleIds = new Set(this.filteredClinics.map((clinic) => clinic.LocationUuid));
      this.selectedClinicIds = this.selectedClinicIds.filter((uuid) => !visibleIds.has(uuid));
    },
    async saveWatchableClinics() {
      this.isSavingClinics = true;
      this.validationError = '';
      try {
        await updateWatchableClinics(this.currentUuid, this.selectedClinicIds);
        await this.loadAssignableClinics();
      } catch (error) {
        this.validationError = error.response?.data?.detail || 'Unable to save watchable clinics.';
      } finally {
        this.isSavingClinics = false;
      }
    },
  },
};
</script>

<style scoped>
.toolbar--compact {
  margin-top: 14px;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.stats-grid--compact {
  margin-top: 14px;
}

.checkbox-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.status-toggle {
  min-height: 44px;
  min-width: 120px;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  background: #f2eef4;
  color: var(--ui-muted);
  font-weight: 800;
}

.status-toggle.active {
  background: #eafaf2;
  color: #167147;
  border-color: rgba(35, 189, 115, 0.24);
}
</style>
